import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Smartphone, 
  MessageSquare, 
  Users, 
  TrendingUp,
  Wifi,
  WifiOff,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { whatsappService } from '../services/whatsappService';
import toast from 'react-hot-toast';

const DashboardHome = () => {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000); // Atualizar a cada 30 segundos
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      const statusData = await whatsappService.getStatus();
      setStatus(statusData);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      setStatus({ success: false, status: 'disconnected' });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'connected') return <Wifi className="h-6 w-6 text-success-600" />;
    if (status === 'disconnected') return <WifiOff className="h-6 w-6 text-danger-600" />;
    return <XCircle className="h-6 w-6 text-gray-400" />;
  };

  const getStatusColor = (status) => {
    if (status === 'connected') return 'text-success-600';
    if (status === 'disconnected') return 'text-danger-600';
    return 'text-gray-500';
  };

  const stats = [
    {
      name: 'Status do WhatsApp',
      value: status?.status || 'Desconhecido',
      icon: getStatusIcon(status?.status),
      color: getStatusColor(status?.status),
      description: status?.isReady ? 'Conectado e funcionando' : 'Desconectado ou em erro'
    },
    {
      name: 'Status da API',
      value: status?.success ? 'OK' : 'Erro',
      icon: status?.success ? <CheckCircle className="h-6 w-6 text-success-600" /> : <XCircle className="h-6 w-6 text-danger-600" />,
      color: status?.success ? 'text-success-600' : 'text-danger-600',
      description: status?.success ? 'API funcionando corretamente' : 'Erro na API'
    },
    {
      name: 'Usuário Conectado',
      value: status?.clientInfo?.pushname || 'N/A',
      icon: <Users className="h-6 w-6 text-primary-600" />,
      color: 'text-primary-600',
      description: status?.clientInfo?.wid?.user || 'Número não disponível'
    },
    {
      name: 'Tempo de Atividade',
      value: status?.timestamp ? new Date(status.timestamp).toLocaleString('pt-BR') : 'N/A',
      icon: <TrendingUp className="h-6 w-6 text-primary-600" />,
      color: 'text-primary-600',
      description: 'Última verificação do sistema'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Visão geral do sistema WhatsApp Service
        </p>
      </div>

      {/* Cards de status */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {stat.icon}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.name}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stat.value}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className={`text-sm ${stat.color}`}>
                {stat.description}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Ações rápidas */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <Link
          to="/dashboard/whatsapp"
          className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Smartphone className="h-8 w-8 text-primary-600 group-hover:text-primary-700" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary-700">
                Controle do WhatsApp
              </h3>
              <p className="text-sm text-gray-500">
                Conectar, desconectar e gerenciar sessões
              </p>
            </div>
          </div>
        </Link>

        <Link
          to="/dashboard/messages"
          className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <MessageSquare className="h-8 w-8 text-primary-600 group-hover:text-primary-700" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary-700">
                Enviar Mensagens
              </h3>
              <p className="text-sm text-gray-500">
                Enviar mensagens de texto, mídia e em lote
              </p>
            </div>
          </div>
        </Link>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Estatísticas
              </h3>
              <p className="text-sm text-gray-500">
                Monitoramento e relatórios do sistema
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Botão de atualizar */}
      <div className="flex justify-center">
        <button
          onClick={loadStatus}
          className="btn-secondary flex items-center"
        >
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
          Atualizar Status
        </button>
      </div>
    </div>
  );
};

export default DashboardHome;

